import { useMemo, useState } from 'react';
import { addDays, format, subDays } from 'date-fns';
import { ConfigContext, SMPArrowButton, SMPFlex, SMPText } from '@velinsi/smp-widgets-ui';

import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { defaultLocaleLang } from '../../../../../packages/constants/date';
import { styled } from '../../../../../packages/theme/stitches.config';
import { useScrollManager, useEvents, useCompetitions, useCompetitionEvents } from '../../hooks';

import {
  BaseWidgetProps,
  CompetitionsDropdownType,
  ICompetitionEvents,
  IMultisportEventsConfig,
  SportTypes,
} from '../../../../../packages/types';

import { getMultisportEventsLabels } from '../../utils/getMultisportLabels';
import { CompetitionList, MultisportDropdowns } from '../../components/common';
import { defaultHandleTournamentClick, extractOnlyDateSDKOptions } from '@sportal365/smp-widget-utils';
import dayjs from 'dayjs';

export type MultisportEventsProps = BaseWidgetProps<IMultisportEventsConfig>;

const Container = styled.withConfig({
  componentId: 'MultisportEventsWidget',
  displayName: 'MultisportContainer',
})('div', {
  display: 'flex',
  width: '100%',
  boxSizing: 'border-box',
  scrollSnapType: 'x mandatory',
  scrollBehavior: 'smooth',
  overflowX: 'scroll',
  // Hide scrollbars (works for most browsers)
  scrollbarWidth: 'none', // For Firefox
  msOverflowStyle: 'none', // For Internet Explorer 10+

  // Hide scrollbars for Chrome, Safari, and newer versions of Edge
  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

function findFirstOdds(data: ICompetitionEvents[]) {
  for (const key of data) {
    const events = key.events; // data[key].competition.events;
    for (const event of events) {
      if (event.odds && event.odds.length > 0) {
        return event.odds; // Return the odds of the first event with non-empty odds array
      }
    }
  }
  return undefined; // Return undefined if no event with non-empty odds is found
}

const DATE_FORMAT = 'yyyy-MM-dd';
export type BookmakerAssetsType = { backgroundColor: string | null; logo: string; url: string; name: string; id: string };

export const MultisportEventsWidget = ({ themes, config, shadow, subscribers, sdkOptions, cuid }: MultisportEventsProps) => {
  const showTopEventsAndCompetitionsOptions =
    (!config?.dataSport && (!config?.dataCompetitions || config.dataCompetitions.length === 0)) ||
    (!config?.dataCompetitions && !Object.values(SportTypes).includes(config?.dataSport ? config.dataSport : SportTypes.ALL));

  const dateFromConfig = config?.dataDate ? dayjs(config?.dataDate?.date, config?.dataDate?.dateFormat).toDate() : null;
  const TODAY_DATE = dateFromConfig || new Date();
  const DATES = [subDays(TODAY_DATE, 1), TODAY_DATE, addDays(TODAY_DATE, 1)];

  const labels = getMultisportEventsLabels(config?.dataLabels);
  const topEventsOption = { label: labels.topEvents, sport: SportTypes.ALL };
  const sdkDateOptions = extractOnlyDateSDKOptions(sdkOptions);
  const localeLang = sdkDateOptions?.dataConfigLang ?? defaultLocaleLang;
  const [selectedCompetition, setSelectedCompetition] = useState<CompetitionsDropdownType>(topEventsOption);
  const date = dayjs(config?.dataDate?.date, config?.dataDate?.dateFormat).toDate();
  const [selectedDate, setSelectedDate] = useState<Date>(config?.dataDate ? date : TODAY_DATE);

  const { competitions, isLoading: isLoadingCompetitions } = useCompetitions({
    date: format(selectedDate, DATE_FORMAT),
    dataRefreshTime: config?.dataRefreshTime,
    showTopEventsAndCompetitionsOptions: showTopEventsAndCompetitionsOptions,
  });

  // Logic for dropdown options menu
  const dataOptionsDisplay = config?.dataOptionsDisplay !== undefined ? config?.dataOptionsDisplay : 'auto';
  const hasOnlyTopEvents = (competitions.length === 1 && competitions[0].sport === SportTypes.ALL) || competitions.length === 0;
  const displayOptions = dataOptionsDisplay === true || (dataOptionsDisplay === 'auto' && !hasOnlyTopEvents);

  const shouldDisplayDropdownContainer =
    displayOptions || (config?.dataOddsBettingId && (config?.dataBettingLogoDisplay || config?.dataOddsDisplay));

  const competitionEventsResult = useCompetitionEvents({
    date: format(selectedDate, DATE_FORMAT),
    competitionIds: config?.dataCompetitions,
    sport: config?.dataSport,
    dataOddsDisplay: config?.dataOddsDisplay || config?.dataBettingLogoDisplay,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataWidgetSport: config?.dataWidgetSport,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetCuid: cuid,
    subscribers: subscribers,
    dataRefreshTime: config?.dataRefreshTime,
    dataTeamNameType: config?.dataTeamNameType,
    dataLimit: config?.dataLimit,
  });

  const eventsResult = useEvents({
    date: format(selectedDate, DATE_FORMAT),
    competitionIds: selectedCompetition.value,
    sport: selectedCompetition.sport,
    dataOddsDisplay: config?.dataOddsDisplay || config?.dataBettingLogoDisplay,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataTeamNameType: config?.dataTeamNameType,
    dataWidgetSport: config?.dataWidgetSport,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetCuid: cuid,
    subscribers: subscribers,
    dataRefreshTime: config?.dataRefreshTime,
    showTopEventsAndCompetitionsOptions: showTopEventsAndCompetitionsOptions,
  });

  // Use the appropriate data based on the configuration
  const {
    competitionEvents,
    isLoading: isLoadingEvents,
    handleClick,
    errorMessage,
  } = useMemo(() => {
    if (config?.dataSport || (config?.dataCompetitions && config.dataCompetitions.length > 0)) {
      return competitionEventsResult;
    } else {
      return eventsResult;
    }
  }, [config, competitionEventsResult, eventsResult]);

  const sportCompetitions = competitionEvents?.competitions ?? [];
  const provider = findFirstOdds(sportCompetitions);
  const bookmakerAssets: BookmakerAssetsType | undefined =
    provider && provider.length > 0 && provider[0].bookmaker?.assets && provider[0].bookmaker?.assets.length > 0
      ? {
          backgroundColor: provider[0].bookmaker?.assets[0].backgroundColor,
          logo: provider[0].bookmaker?.assets[0].logo,
          url: provider[0].bookmaker.url,
          name: provider[0].bookmaker.name,
          id: provider[0].bookmaker.id,
        }
      : undefined;

  const handleCompetitionClick = ({ id, name }: { id?: string; name: string }) => defaultHandleTournamentClick(handleClick, id ?? '', name);

  const { containerRef, canScrollLeft, canScrollRight, handleLeftClick, handleRightClick } = useScrollManager({
    isLoadingEvents,
    isLoadingCompetitions,
    competitionEvents,
    handleClick,
    competitions: config?.dataCompetitions,
  });

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions, dataOptionsDisplay: displayOptions }}>
      <ThemeProvider
        css={{ background: '$multiSportBgColor' }}
        slickStyles={true}
        shadow={shadow}
        themes={themes}
        dataTheme={config?.dataTheme}
        isLoading={isLoadingEvents || isLoadingCompetitions}
        removeMaxWidth
        loaderMinHeight={config?.dataOddsDisplay ? '105px' : '80px'}
      >
        <SMPFlex>
          {shouldDisplayDropdownContainer && (
            <MultisportDropdowns
              selectedDate={selectedDate}
              dates={DATES}
              handleDateChange={(date) => {
                setSelectedDate(date);
                setSelectedCompetition(topEventsOption);
              }}
              competitions={competitions}
              selectedCompetition={selectedCompetition}
              handleCompetitionChange={(value) => {
                setSelectedCompetition(value);
                handleCompetitionClick({ id: value.value, name: value.label });
              }}
              topEventsOption={topEventsOption}
              bookmakerAssets={bookmakerAssets}
              localeLang={localeLang}
              handleClick={handleClick}
              showTopEventsAndCompetitionsOptions={showTopEventsAndCompetitionsOptions}
            />
          )}
          {errorMessage && (
            <SMPText style={{ padding: '18px' }} variant='contrast' size='3' weight='700'>
              {errorMessage}
            </SMPText>
          )}
          {canScrollLeft && <SMPArrowButton variant='left' onClick={handleLeftClick} />}
          <Container ref={containerRef}>
            {sportCompetitions.map((competition) => (
              <CompetitionList
                topEventsSelected={selectedCompetition.value === undefined}
                key={competition.competition.id}
                competition={competition}
                dataEntityLinks={config?.dataEntityLinks}
                sdkDateOptions={sdkDateOptions}
                handleClick={handleClick}
              />
            ))}
          </Container>
          {canScrollRight && <SMPArrowButton variant='right' onClick={handleRightClick} />}
        </SMPFlex>
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
